import { Dispatch, SetStateAction, useMemo } from 'react'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useScanValidation } from 'hooks/queries/useScanValidation'
import { MappedErrorMessageState } from 'types/errors'

type UseBankDetailValidationProps = {
  intlIban?: string
  bicSwiftCode?: string
  routingNumber?: string
  sortCode?: string
  accountNumber?: string
  setRequestError?: Dispatch<SetStateAction<MappedErrorMessageState>>
}

export const useBankDetailValidation = ({
  intlIban,
  bicSwiftCode,
  routingNumber,
  sortCode,
  accountNumber,
  setRequestError,
}: UseBankDetailValidationProps) => {
  const ibanValidation = useIbanValidation(intlIban, 'IBAN', setRequestError, !!intlIban)
  const bicValidation = useBicValidation(bicSwiftCode, setRequestError, !!bicSwiftCode)
  const routingNumberValidation = useRoutingNumberValidation(
    routingNumber,
    setRequestError,
    !!routingNumber
  )

  const scanValidation = useScanValidation(
    sortCode,
    accountNumber,
    setRequestError,
    !!(sortCode && accountNumber)
  )

  return useMemo(
    () => ({
      // IBAN validation
      validateIban: ibanValidation?.refetch,
      isValidatingIban: ibanValidation?.isFetching,
      isValidatingIbanError: ibanValidation?.isError,
      ibanBankDetails: ibanValidation?.data,

      // BIC validation
      validateBic: bicValidation?.refetch,
      isValidatingBic: bicValidation?.isFetching,
      isValidatingBicError: bicValidation?.isError,
      bicValidation: bicValidation?.data,

      // Routing number validation
      validateRoutingNumber: routingNumberValidation?.refetch,
      isValidatingRoutingNumber: routingNumberValidation?.isFetching,
      isValidatingRoutingNumberError: routingNumberValidation?.isError,
      usBankDetails: routingNumberValidation?.data,

      // SCAN validation
      validateScan: scanValidation?.refetch,
      isValidatingScan: scanValidation?.isFetching,
      isValidatingScanError: scanValidation?.isError,
      scanBankDetails: scanValidation?.data,
    }),
    [ibanValidation, bicValidation, routingNumberValidation, scanValidation]
  )
}
